import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Sparkles, Trash2, ExternalLink, Plus, Rocket, RefreshCw, ChevronRight, ArrowLeft, ThumbsDown, Wand2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
    generating: 'bg-amber-500/20 text-amber-700 dark:text-amber-300',
    draft: 'bg-sky-500/20 text-sky-700 dark:text-sky-300',
    publishing: 'bg-violet-500/20 text-violet-700 dark:text-violet-300',
    published: 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300',
    failed: 'bg-red-500/20 text-red-700 dark:text-red-300',
};

/**
 * AdminCuratorPage — /admin/curator
 *
 * Two-pane admin surface: left = subject input + draft list, right =
 * currently-selected draft in an editable inline form. Publish button
 * ingests each URL as a bookmark and creates the public collection on
 * the BrainSync brand account.
 *
 * Access is enforced server-side by `require_admin` on every endpoint;
 * this page just hides itself for non-admins to avoid confusion.
 */
export default function AdminCuratorPage() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [drafts, setDrafts] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [selected, setSelected] = useState(null);
    const [subject, setSubject] = useState('');
    const [count, setCount] = useState(10);
    const [busy, setBusy] = useState(false);
    // Suggestion pool (Option A: subject-only ideas, refreshed daily).
    const [pool, setPool] = useState([]);
    const [poolLoading, setPoolLoading] = useState(false);
    const [poolActionId, setPoolActionId] = useState(null);

    // ------------------------------------------------------------------
    // Data loading
    // ------------------------------------------------------------------

    const loadDrafts = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/api/admin/curator/drafts`, { withCredentials: true });
            setDrafts(data.drafts || []);
        } catch (e) {
            if (e.response?.status === 403) {
                toast.error("You don't have access to the curator");
                navigate('/dashboard');
            }
        }
    }, [navigate]);

    const loadSelected = useCallback(async (id) => {
        if (!id) { setSelected(null); return; }
        try {
            const { data } = await axios.get(`${API}/api/admin/curator/drafts/${id}`, { withCredentials: true });
            setSelected(data);
        } catch {
            setSelected(null);
        }
    }, []);

    useEffect(() => { loadDrafts(); }, [loadDrafts]);
    useEffect(() => { loadSelected(selectedId); }, [selectedId, loadSelected]);

    // Suggestion pool — load on mount and expose manual refresh.
    const loadPool = useCallback(async (forceRefresh = false) => {
        setPoolLoading(true);
        try {
            const url = `${API}/api/admin/curator/pool${forceRefresh ? '?refresh=1' : ''}`;
            const { data } = await axios.get(url, { withCredentials: true });
            setPool(data.suggestions || []);
        } catch (e) {
            if (e.response?.status !== 403) toast.error('Failed to load suggestions');
        } finally { setPoolLoading(false); }
    }, []);

    useEffect(() => { loadPool(false); }, [loadPool]);

    const dismissSuggestion = async (suggestionId) => {
        setPoolActionId(suggestionId);
        try {
            await axios.post(`${API}/api/admin/curator/pool/${suggestionId}/dismiss`, {}, { withCredentials: true });
            setPool((p) => p.filter((s) => s.suggestion_id !== suggestionId));
        } catch { toast.error('Dismiss failed'); }
        finally { setPoolActionId(null); }
    };

    const generateFromSuggestion = async (suggestion) => {
        setPoolActionId(suggestion.suggestion_id);
        try {
            const { data } = await axios.post(
                `${API}/api/admin/curator/pool/${suggestion.suggestion_id}/generate`,
                { count },
                { withCredentials: true },
            );
            toast.success(`Generating "${suggestion.subject.slice(0, 40)}…"`);
            setPool((p) => p.filter((s) => s.suggestion_id !== suggestion.suggestion_id));
            setSelectedId(data.draft_id);
            await loadDrafts();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Generate failed');
        } finally { setPoolActionId(null); }
    };

    // Poll for status changes on the selected draft when it's actively
    // generating, publishing, OR when bookmarks are still processing
    // in the background (publish is fire-and-forget — the draft flips
    // to `published` well before Whisper/yt-dlp finish per-entry).
    // Cheap: one GET every 3s.
    useEffect(() => {
        if (!selected) return;
        const isTransient = ['generating', 'publishing'].includes(selected.status);
        const stillProcessing = (selected.progress?.processing || 0) > 0;
        if (!isTransient && !stillProcessing) return;
        const t = setInterval(() => loadSelected(selectedId), 3000);
        return () => clearInterval(t);
    }, [selected, selectedId, loadSelected]);

    // ------------------------------------------------------------------
    // Mutations
    // ------------------------------------------------------------------

    const generate = async () => {
        const s = subject.trim();
        if (s.length < 3) { toast.error('Subject is too short'); return; }
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/api/admin/curator/generate`, { subject: s, count }, { withCredentials: true });
            toast.success('Generation started — usually 5-15s');
            setSubject('');
            setSelectedId(data.draft_id);
            await loadDrafts();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to start');
        } finally { setBusy(false); }
    };

    const patchEntry = async (position, changes) => {
        if (!selected) return;
        try {
            await axios.patch(`${API}/api/admin/curator/drafts/${selected.draft_id}/entries/${position}`, changes, { withCredentials: true });
            await loadSelected(selected.draft_id);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Update failed');
        }
    };

    const deleteEntry = async (position) => {
        if (!selected || !window.confirm(`Delete entry #${position}?`)) return;
        try {
            await axios.delete(`${API}/api/admin/curator/drafts/${selected.draft_id}/entries/${position}`, { withCredentials: true });
            await loadSelected(selected.draft_id);
            toast.success('Entry removed');
        } catch { toast.error('Delete failed'); }
    };

    const addEntry = async () => {
        if (!selected) return;
        const title = window.prompt('Entry title:');
        if (!title) return;
        const url = window.prompt('Entry URL:');
        if (!url) return;
        try {
            await axios.post(`${API}/api/admin/curator/drafts/${selected.draft_id}/entries`, { title, url }, { withCredentials: true });
            await loadSelected(selected.draft_id);
        } catch (e) { toast.error(e.response?.data?.detail || 'Add failed'); }
    };

    const publishDraft = async () => {
        if (!selected) return;
        if (!window.confirm(`Publish "${selected.title}" to brainsync.pro/brainsync? This will ingest ${selected.entries?.length || 0} URLs and create the public collection.`)) return;
        setBusy(true);
        try {
            await axios.post(`${API}/api/admin/curator/drafts/${selected.draft_id}/publish`, {}, { withCredentials: true });
            toast.success('Publish started — check back in ~30s. Status updates automatically.');
            await loadSelected(selected.draft_id);
            await loadDrafts();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Publish failed');
        } finally { setBusy(false); }
    };

    const retryFailed = async () => {
        if (!selected) return;
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/api/admin/curator/drafts/${selected.draft_id}/retry-failed`, {}, { withCredentials: true });
            if ((data.retried || 0) === 0) {
                toast.info(data.note || 'Nothing to retry');
            } else {
                toast.success(`Retrying ${data.retried} bookmark(s)…`);
            }
            await loadSelected(selected.draft_id);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Retry failed');
        } finally { setBusy(false); }
    };

    const discardDraft = async () => {
        if (!selected || !window.confirm('Discard this draft permanently?')) return;
        try {
            await axios.delete(`${API}/api/admin/curator/drafts/${selected.draft_id}`, { withCredentials: true });
            setSelectedId(null);
            await loadDrafts();
            toast.success('Draft discarded');
        } catch { toast.error('Discard failed'); }
    };

    if (authLoading) return <div className="p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;
    if (!user) { navigate('/'); return null; }

    // ------------------------------------------------------------------
    // Render
    // ------------------------------------------------------------------

    return (
        <div className="min-h-screen bg-background text-foreground">
            <div className="max-w-[1400px] mx-auto p-6 lg:p-8">
                <div className="flex items-center gap-3 mb-6">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="curator-back-btn">
                        <ArrowLeft className="w-4 h-4 mr-1" /> Admin
                    </Button>
                    <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight">AI Content Curator</h1>
                    <Badge variant="secondary" className="ml-2">Beta</Badge>
                </div>

                <div className="grid lg:grid-cols-[380px_1fr] gap-6">
                    {/* LEFT PANE — new + draft list */}
                    <aside className="space-y-6">
                        <div className="rounded-2xl border border-border bg-card p-5 space-y-3">
                            <div className="flex items-center gap-2 text-sm font-semibold">
                                <Sparkles className="w-4 h-4 text-sky-500" /> New collection
                            </div>
                            <Input
                                placeholder='e.g. "Top 10 greatest Messi goals"'
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && generate()}
                                data-testid="curator-subject-input"
                            />
                            <div className="flex items-center gap-3">
                                <label className="text-xs text-muted-foreground">Count</label>
                                <Input
                                    type="number"
                                    min={3} max={25}
                                    value={count}
                                    onChange={(e) => setCount(parseInt(e.target.value, 10) || 10)}
                                    className="w-20"
                                    data-testid="curator-count-input"
                                />
                                <Button
                                    onClick={generate}
                                    disabled={busy || subject.trim().length < 3}
                                    className="ml-auto"
                                    data-testid="curator-generate-btn"
                                >
                                    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
                                    Generate
                                </Button>
                            </div>
                            <p className="text-[11px] text-muted-foreground leading-relaxed">
                                Runs Claude Sonnet 4.5 with your content-curator skill + Brave Web Search.
                                Typical run: 15-25 web searches, ~5-15s. Cost ≈ $0.15-0.30 per collection.
                            </p>
                        </div>

                        {/* Suggestion pool — daily brainstormed ideas */}
                        <div className="rounded-2xl border border-border bg-card" data-testid="curator-pool">
                            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
                                <div className="flex items-center gap-2 text-sm font-semibold">
                                    <Wand2 className="w-4 h-4 text-violet-500" />
                                    Today&apos;s ideas
                                    <span className="text-[11px] text-muted-foreground font-normal">
                                        ({pool.length})
                                    </span>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => loadPool(true)}
                                    disabled={poolLoading}
                                    data-testid="curator-pool-refresh"
                                    title="Force refresh — asks Claude for a fresh batch"
                                >
                                    {poolLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                </Button>
                            </div>
                            <div className="max-h-[45vh] overflow-y-auto divide-y divide-border">
                                {poolLoading && pool.length === 0 && (
                                    <div className="p-6 text-sm text-muted-foreground text-center flex items-center justify-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin" /> Brainstorming subjects…
                                    </div>
                                )}
                                {!poolLoading && pool.length === 0 && (
                                    <div className="p-6 text-sm text-muted-foreground text-center">
                                        No fresh ideas. Tap refresh to generate.
                                    </div>
                                )}
                                {pool.map((s) => (
                                    <div key={s.suggestion_id} className="p-4 space-y-2 hover:bg-muted/30 transition-colors" data-testid={`curator-pool-item-${s.suggestion_id}`}>
                                        <div className="flex items-start gap-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium leading-snug">{s.subject}</div>
                                                {s.theme && (
                                                    <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-700 dark:text-violet-300 font-medium uppercase tracking-wide">
                                                        {s.theme}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {s.rationale && (
                                            <div className="text-[11px] text-muted-foreground leading-snug italic">
                                                {s.rationale}
                                            </div>
                                        )}
                                        <div className="flex items-center gap-2 pt-1">
                                            <Button
                                                size="sm"
                                                onClick={() => generateFromSuggestion(s)}
                                                disabled={poolActionId === s.suggestion_id}
                                                className="h-7 px-2.5 text-xs bg-violet-600 hover:bg-violet-700 flex-1"
                                                data-testid={`curator-pool-generate-${s.suggestion_id}`}
                                            >
                                                {poolActionId === s.suggestion_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Sparkles className="w-3 h-3 mr-1" /> Generate</>}
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => dismissSuggestion(s.suggestion_id)}
                                                disabled={poolActionId === s.suggestion_id}
                                                className="h-7 w-7 p-0 text-muted-foreground hover:text-red-500"
                                                data-testid={`curator-pool-dismiss-${s.suggestion_id}`}
                                                title="Dismiss (permanent — Claude won't suggest this again)"
                                            >
                                                <ThumbsDown className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="rounded-2xl border border-border bg-card">
                            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
                                <div className="text-sm font-semibold">Drafts</div>
                                <Button variant="ghost" size="sm" onClick={loadDrafts} data-testid="curator-refresh-drafts">
                                    <RefreshCw className="w-3.5 h-3.5" />
                                </Button>
                            </div>
                            <div className="max-h-[70vh] overflow-y-auto divide-y divide-border" data-testid="curator-draft-list">
                                {drafts.length === 0 && (
                                    <div className="p-6 text-sm text-muted-foreground text-center">No drafts yet</div>
                                )}
                                {drafts.map((d) => (
                                    <button
                                        key={d.draft_id}
                                        onClick={() => setSelectedId(d.draft_id)}
                                        className={`w-full text-left p-4 hover:bg-muted/40 transition-colors ${selectedId === d.draft_id ? 'bg-muted/60' : ''}`}
                                        data-testid={`curator-draft-item-${d.draft_id}`}
                                    >
                                        <div className="flex items-start gap-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium truncate">{d.title || d.subject}</div>
                                                <div className="text-[11px] text-muted-foreground truncate mt-0.5">{d.subject}</div>
                                                <div className="flex items-center gap-2 mt-2">
                                                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[d.status] || 'bg-muted'}`}>
                                                        {d.status}
                                                    </span>
                                                    <span className="text-[10px] text-muted-foreground">
                                                        {(d.entries || []).length} entries
                                                    </span>
                                                </div>
                                            </div>
                                            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </aside>

                    {/* RIGHT PANE — selected draft editor */}
                    <main className="min-w-0">
                        {!selected && (
                            <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center text-muted-foreground" data-testid="curator-empty-state">
                                <Sparkles className="w-8 h-8 mx-auto mb-3 opacity-40" />
                                Pick a draft on the left, or start a new one.
                            </div>
                        )}

                        {selected && selected.status === 'generating' && (
                            <div className="rounded-2xl border border-border bg-card p-12 text-center" data-testid="curator-generating">
                                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-sky-500" />
                                <div className="text-sm text-muted-foreground">
                                    Curating <span className="font-semibold text-foreground">{selected.subject}</span>…
                                </div>
                                <div className="text-[11px] text-muted-foreground mt-2">
                                    Planning + Brave searches + curation. Usually 5-15 seconds.
                                </div>
                            </div>
                        )}

                        {selected && selected.status === 'failed' && (
                            <div className="rounded-2xl border border-red-500/40 bg-red-500/5 p-8" data-testid="curator-failed">
                                <div className="text-sm font-semibold text-red-600 dark:text-red-400 mb-2">Generation failed</div>
                                <div className="text-xs text-muted-foreground font-mono whitespace-pre-wrap break-words">{selected.error || 'Unknown error'}</div>
                                <Button className="mt-4" variant="outline" size="sm" onClick={discardDraft} data-testid="curator-discard-failed">
                                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Discard
                                </Button>
                            </div>
                        )}

                        {selected && ['draft', 'publishing', 'published'].includes(selected.status) && (
                            <DraftEditor
                                draft={selected}
                                onPatchEntry={patchEntry}
                                onDeleteEntry={deleteEntry}
                                onAddEntry={addEntry}
                                onPublish={publishDraft}
                                onRetryFailed={retryFailed}
                                onDiscard={discardDraft}
                                busy={busy}
                            />
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
}


function DraftEditor({ draft, onPatchEntry, onDeleteEntry, onAddEntry, onPublish, onRetryFailed, onDiscard, busy }) {
    const isPublished = draft.status === 'published';
    // Some drafts get stuck in `publishing` state (e.g. HTTP handler
    // interrupted by a redeploy mid-loop). The category and bookmarks
    // may already exist — so we treat "has a category_id" as the real
    // signal for showing View-live + Retry-failed affordances.
    const hasLiveCategory = !!draft.category_id;
    const canEditEntries = !isPublished && draft.status !== 'publishing';
    // Live ingestion progress — populated by the backend once
    // `category_id` exists. Bookmarks process asynchronously after
    // publish, so `processing > 0` can be true even when the draft
    // itself is already `published`.
    const progress = draft.progress;
    const showProgress = hasLiveCategory && progress && progress.planned > 0;
    const isIngesting = showProgress && progress.processing > 0;
    return (
        <div className="space-y-5">
            {/* Header card */}
            <div className="rounded-2xl border border-border bg-card p-6">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[draft.status]}`}>{draft.status}</span>
                            <span className="text-[11px] text-muted-foreground">from “{draft.subject}”</span>
                        </div>
                        <h2 className="font-heading text-xl lg:text-2xl font-bold" data-testid="curator-draft-title">{draft.title}</h2>
                        {draft.description && <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{draft.description}</p>}
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                        {!isPublished && (
                            <Button onClick={onPublish} disabled={busy || draft.status === 'publishing'} className="bg-emerald-600 hover:bg-emerald-700" data-testid="curator-publish-btn">
                                {busy || draft.status === 'publishing' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Rocket className="w-4 h-4 mr-1" />}
                                {draft.status === 'publishing' ? 'Publishing…' : 'Publish'}
                            </Button>
                        )}
                        {hasLiveCategory && (
                            <a
                                href={`/c/${draft.category_id}`}
                                target="_blank" rel="noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-sky-600 hover:underline"
                                data-testid="curator-view-published-link"
                            >
                                View live <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                        )}
                        {hasLiveCategory && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={onRetryFailed}
                                disabled={busy}
                                className="gap-1.5"
                                data-testid="curator-retry-failed-btn"
                                title="Reprocess any bookmarks in this collection that are currently in Failed state"
                            >
                                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                Retry failed
                            </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={onDiscard} data-testid="curator-discard-btn">
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Discard
                        </Button>
                    </div>
                </div>
                {showProgress && (
                    <ProgressBanner progress={progress} isIngesting={isIngesting} />
                )}
                {draft.transparency_note && (
                    <div className="mt-4 pt-4 border-t border-border">
                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Transparency note</div>
                        <div className="text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap">{draft.transparency_note}</div>
                    </div>
                )}
            </div>

            {/* Entries */}
            <div className="space-y-3" data-testid="curator-entries-list">
                {(draft.entries || []).map((e) => (
                    <EntryRow
                        key={e.position}
                        entry={e}
                        readonly={!canEditEntries}
                        onPatch={(changes) => onPatchEntry(e.position, changes)}
                        onDelete={() => onDeleteEntry(e.position)}
                    />
                ))}
                {canEditEntries && (
                    <Button variant="outline" onClick={onAddEntry} className="w-full" data-testid="curator-add-entry-btn">
                        <Plus className="w-4 h-4 mr-1" /> Add entry
                    </Button>
                )}
            </div>
        </div>
    );
}


function ProgressBanner({ progress, isIngesting }) {
    const { planned, ingested, processing, failed } = progress;
    // Progress bar reflects "finished" state (ingested + failed vs planned).
    const finished = ingested + failed;
    const pct = planned > 0 ? Math.min(100, Math.round((finished / planned) * 100)) : 0;
    // Tone: still ingesting → sky, all done with failures → amber,
    // all done clean → emerald.
    const tone = isIngesting
        ? 'sky'
        : failed > 0 ? 'amber' : 'emerald';
    const toneClasses = {
        sky: 'border-sky-500/30 bg-sky-500/10 text-sky-800 dark:text-sky-200',
        amber: 'border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-200',
        emerald: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200',
    };
    const barTone = {
        sky: 'bg-sky-500',
        amber: 'bg-amber-500',
        emerald: 'bg-emerald-500',
    };
    return (
        <div
            className={`mt-4 pt-4 border-t border-border`}
            data-testid="curator-progress-banner"
        >
            <div className={`rounded-lg border p-3 ${toneClasses[tone]}`}>
                <div className="flex items-center gap-2 text-sm font-medium">
                    {isIngesting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    <span data-testid="curator-progress-summary">
                        {isIngesting
                            ? `Ingesting ${ingested} of ${planned}${failed > 0 ? ` · ${failed} failed` : ''}${processing > 0 ? ` · ${processing} in flight` : ''}`
                            : failed > 0
                                ? `${ingested} of ${planned} ingested · ${failed} failed`
                                : `All ${ingested} of ${planned} ingested cleanly`}
                    </span>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-black/10 dark:bg-white/10 overflow-hidden">
                    <div
                        className={`h-full transition-all duration-500 ${barTone[tone]}`}
                        style={{ width: `${pct}%` }}
                        data-testid="curator-progress-bar"
                    />
                </div>
                {isIngesting && (
                    <div className="text-[11px] mt-1.5 opacity-80">
                        Each URL runs through OG scrape + optional Whisper transcript. This can take a few minutes for video-heavy collections.
                    </div>
                )}
            </div>
        </div>
    );
}


function EntryRow({ entry, readonly, onPatch, onDelete }) {
    const [title, setTitle] = useState(entry.title || '');
    const [url, setUrl] = useState(entry.url || '');
    const [why, setWhy] = useState(entry.why || '');
    const [dirty, setDirty] = useState(false);

    // Reset the local editable state when the parent-provided entry
    // changes (e.g. after a delete + reindex or a fresh load).
    useEffect(() => {
        setTitle(entry.title || '');
        setUrl(entry.url || '');
        setWhy(entry.why || '');
        setDirty(false);
    }, [entry.position, entry.title, entry.url, entry.why]);

    const save = async () => {
        await onPatch({ title, url, why });
        setDirty(false);
    };

    return (
        <div className="rounded-xl border border-border bg-card p-4 flex gap-3" data-testid={`curator-entry-${entry.position}`}>
            <div className="shrink-0 w-10 h-10 rounded-full bg-sky-500/15 text-sky-600 dark:text-sky-400 flex items-center justify-center font-bold text-sm">
                {entry.position}
            </div>
            <div className="flex-1 min-w-0 space-y-2">
                {readonly ? (
                    <>
                        <div className="font-medium">{entry.title}</div>
                        <a href={entry.url} target="_blank" rel="noreferrer" className="text-xs text-sky-600 hover:underline break-all inline-flex items-center gap-1">
                            {entry.url} <ExternalLink className="w-3 h-3" />
                        </a>
                        {entry.why && <div className="text-xs text-muted-foreground leading-relaxed">{entry.why}</div>}
                    </>
                ) : (
                    <>
                        <Input value={title} onChange={(e) => { setTitle(e.target.value); setDirty(true); }} placeholder="Title" className="font-medium" data-testid={`curator-entry-title-${entry.position}`} />
                        <Input value={url} onChange={(e) => { setUrl(e.target.value); setDirty(true); }} placeholder="URL" className="font-mono text-xs" data-testid={`curator-entry-url-${entry.position}`} />
                        <Textarea value={why} onChange={(e) => { setWhy(e.target.value); setDirty(true); }} placeholder="Why this made the list…" rows={2} className="text-xs" data-testid={`curator-entry-why-${entry.position}`} />
                    </>
                )}
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    {entry.platform && <span className="px-1.5 py-0.5 rounded bg-muted">{entry.platform}</span>}
                    {entry.uploader && <span>· {entry.uploader}</span>}
                    {entry.is_official && <span className="text-emerald-600 font-medium">· official ✓</span>}
                    {entry.duration && <span>· {entry.duration}</span>}
                    {entry.engagement_hint && <span>· {entry.engagement_hint}</span>}
                </div>
            </div>
            {!readonly && (
                <div className="flex flex-col gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={save} disabled={!dirty} data-testid={`curator-entry-save-${entry.position}`}>Save</Button>
                    <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-600" data-testid={`curator-entry-delete-${entry.position}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                </div>
            )}
        </div>
    );
}
